import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || 'love');
  const categoryComics = getComicsByCategory(slug || 'love');

  const [activeCategory, setActiveCategory] = useState(slug || 'love');
  const [displayComics, setDisplayComics] = useState(categoryComics);

  useEffect(() => {
    setActiveCategory(slug || 'love');
    const newCat = getCategoryBySlug(slug || 'love');
    setDisplayComics(getComicsByCategory(slug || 'love'));
  }, [slug]);

  const categoryNameMap: Record<string, string> = {
    love: '恋爱', action: '动作', fantasy: '奇幻', thriller: '悬疑',
    comedy: '喜剧', drama: '剧情', school: '校园', bl: '耽美', horror: '恐怖',
  };

  return (
    <>
      <SEO
        title={`${category.name}漫画`}
        description={`可游漫画网${category.name}分类 - ${category.description}。提供最全的韩国${category.name}漫画在线免费阅读，下拉式流畅体验，无删减原版内容。`}
        keywords={`韩国漫画,${category.name}漫画,韩漫在线免费阅读,可游漫画网,无删减漫画,下拉式`}
        canonicalPath={`/category/${slug}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: `${category.name}漫画 - 可游漫画网`,
        description: `浏览可游漫画网${category.name}分类下的所有韩国漫画`,
        url: `https://www.keyoumanhua.com/category/${slug}`,
      }} />
      <ScrollToTop />

      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="bg-background-100 py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/love' },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-xl md:text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-800">
                  韩国{category.name}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description} · 共 {displayComics.length} 部作品</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-4 md:py-6 px-4 md:px-6 border-b border-background-200/70 sticky top-16 bg-background-50/95 backdrop-blur-md z-40">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Comics Grid */}
        <section className="py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-[1440px] mx-auto">
            {displayComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5" data-product-shop>
                {displayComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，去看看其他分类吧</p>
                <Link
                  to="/category/love"
                  className="inline-block mt-4 px-6 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}